import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem, Alert, Typography,
} from '@mui/material';
import { Upload } from '@mui/icons-material';
import { useFormik } from 'formik';
import { expenseSchema } from './expenseSchema';
import PaymentMethodFields from '../common/PaymentMethodFields';
import { ExpenseCategory, ExpenseSubCategory } from '../../types';
import expenseCategoryService from '../../services/expenseCategoryService';
import { useAuth } from '../../context/AuthContext';
import { useTimezone } from '../../hooks/useTimezone';
import { formatFileSize } from '../../utils/fileSize';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onSubmit: (data: FormData) => Promise<void>;
}

const validationSchema = expenseSchema;

const CreateExpenseDialog: React.FC<Props> = ({ open, onClose, onSuccess, onSubmit }) => {
  const { currency } = useAuth();
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [subCategories, setSubCategories] = useState<ExpenseSubCategory[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      expenseCategoryService.getCategories().then(setCategories).catch(() => {});
      setSubCategories([]);
      setFile(null);
    }
  }, [open]);

  // An expense can't be dated in the future — the picker stops at today (school time).
  const { getToday } = useTimezone();
  const formik = useFormik({
    initialValues: {
      title: '',
      amount: '',
      date: getToday(),
      categoryId: '',
      subCategoryId: '',
      notes: '',
      method: 'cash',
      referenceNumber: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append('title', values.title);
        formData.append('amount', values.amount.toString());
        formData.append('date', values.date);
        if (values.categoryId) formData.append('categoryId', values.categoryId);
        if (values.subCategoryId) formData.append('subCategoryId', values.subCategoryId);
        if (values.notes) formData.append('notes', values.notes);
        formData.append('method', values.method);
        if (values.referenceNumber.trim()) formData.append('referenceNumber', values.referenceNumber.trim());
        if (file) formData.append('expenseDocument', file);

        await onSubmit(formData);
        formik.resetForm();
        setFile(null);
        onSuccess();
      } catch (error: any) {
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCategoryChange = async (categoryId: string) => {
    formik.setFieldValue('categoryId', categoryId);
    formik.setFieldValue('subCategoryId', '');
    if (categoryId) {
      try {
        const subs = await expenseCategoryService.getSubCategories(categoryId);
        setSubCategories(subs);
      } catch {
        setSubCategories([]);
      }
    } else {
      setSubCategories([]);
    }
  };

  const handleClose = () => {
    formik.resetForm();
    setFile(null);
    setSubCategories([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Expense</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                name="title"
                label="Title"
                value={formik.values.title}
                onChange={formik.handleChange}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="amount"
                label={`Amount (${currency})`}
                type="number"
                value={formik.values.amount}
                onChange={formik.handleChange}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
                margin="normal"
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="date"
                label="Date"
                type="date"
                value={formik.values.date}
                onChange={formik.handleChange}
                error={formik.touched.date && Boolean(formik.errors.date)}
                helperText={formik.touched.date && formik.errors.date}
                margin="normal"
                inputProps={{ max: getToday() }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl
                fullWidth
                margin="normal"
                error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
              >
                <InputLabel>Category *</InputLabel>
                <Select
                  value={formik.values.categoryId}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  onBlur={() => formik.setFieldTouched('categoryId', true)}
                  label="Category *"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
                  ))}
                </Select>
                {formik.touched.categoryId && formik.errors.categoryId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formik.errors.categoryId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth margin="normal" disabled={!formik.values.categoryId || subCategories.length === 0}>
                <InputLabel>Sub-Category</InputLabel>
                <Select
                  value={formik.values.subCategoryId}
                  onChange={formik.handleChange}
                  name="subCategoryId"
                  label="Sub-Category"
                >
                  <MenuItem value="">None</MenuItem>
                  {subCategories.map((sub) => (
                    <MenuItem key={sub._id} value={sub._id}>{sub.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <PaymentMethodFields formik={formik} label="Paid by" />
            <Grid size={12}>
              <TextField
                fullWidth
                name="notes"
                label="Notes"
                multiline
                rows={3}
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
                margin="normal"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                type="file"
                label="Receipt (Image/PDF)"
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: 'image/*,application/pdf' }}
                onChange={(e: any) => setFile(e.target.files?.[0] || null)}
                margin="normal"
              />
              {file && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Selected: {file.name} ({formatFileSize(file.size)})
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={<Upload />} disabled={loading}>
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateExpenseDialog;
